import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { assetsApi, type AssetFilter, type AssetSearchFacets, type AssetSummary } from "../api/assets";
import { assetTypesApi, attributesApi, type AttributeDataType, type AttributeDefView, type AttributeView } from "../api/catalog";
import { locationsApi } from "../api/locations";
import type { ApiError } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import {
  ATTRIBUTE_PREFIX, attributeSearchField, decodeFilters, encodeFilters, fieldTitle, fixedSearchFields,
  rangeProblem, toRequestFilters, type FilterState, type SearchField, type SearchFieldGroup,
} from "../lib/assetSearch";
import { flattenLocations, locationSelectOptions, type FlatLocation } from "../lib/locationsFlat";
import { useLocationKinds } from "../lib/useLocationKinds";
import { shortPathLabel } from "../lib/paths";
import { AssetFilterRow } from "./AssetFilterRow";
import { formatValue } from "./AssetAttributesTable";
import { ConditionChip, UsageChip } from "./ConditionChip";
import { ExportButton } from "./ExportButton";
import { LocationKindIcon } from "./LocationKindIcon";
import { PermLink } from "./PermLink";
import { Select, type SelectOption } from "./Select";
import { SortableTh, sortParam, type SortState } from "./SortableTh";
import { SERVER_ALL_LIMIT, TableFooter, readPageSize, requestPageSize, storePageSize, type PageSize } from "./TableFooter";
import Ic from "./icons.jsx";

const PAGE_SIZE_KEY = "bkm.advSearch.pageSize";

/** Typing in a text or range box settles for this long before the search runs. */
const DEBOUNCE_MS = 250;

/** Attribute columns of at most this many picked types are added to the table. */
const MAX_TYPE_COLUMNS = 5;

type AttributeColumn = { key: string; label: string; unit: string | null; dataType: AttributeDataType };

/**
 * "Napredna pretraga" view of the part search (sprint 5i): criteria are added
 * one by one — a column of the register, a branch of the position or location
 * tree, any attribute from the codebook — and all of them must hold. The table
 * follows every change; each criterion offers only what the others leave, with
 * counts. The criteria live in the `f` query parameter, so a search can be
 * linked and survives "back".
 */
export function AdvancedAssetSearch() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const canExport = hasPermission("assets:export");
  const [params, setParams] = useSearchParams();

  const [filters, setFilters] = useState<FilterState[]>(() => {
    const fromLink = decodeFilters(params.get("f"));
    if (fromLink.length > 0) return fromLink;
    // Arriving from the other views with a type picked: start from that type.
    const typeId = params.get("typeId");
    return typeId ? [{ field: "typeId", values: [typeId] }] : [];
  });
  const [attributes, setAttributes] = useState<AttributeView[] | null>(null);
  const [locations, setLocations] = useState<FlatLocation[]>([]);
  const [facets, setFacets] = useState<AssetSearchFacets | null>(null);
  const [rows, setRows] = useState<AssetSummary[] | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sort, setSort] = useState<SortState<string>>({ field: "tag", dir: "asc" });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<PageSize>(() => readPageSize(PAGE_SIZE_KEY, 20));
  const [typeDefs, setTypeDefs] = useState<Record<string, AttributeDefView[]>>({});

  const canSeeStructure = hasPermission("structure:view");
  const canSeeLocations = hasPermission("locations:view");

  useEffect(() => {
    attributesApi.list().then(setAttributes).catch(() => setAttributes([]));
  }, []);

  useEffect(() => {
    if (!canSeeLocations) return;
    locationsApi.tree().then((tree) => setLocations(flattenLocations(tree))).catch(() => setLocations([]));
  }, [canSeeLocations]);

  // Every field the caller may filter by: the fixed ones (trees only with their view) and the codebook attributes.
  const fields = useMemo<SearchField[]>(() => {
    const allowed: Record<string, boolean> = { "structure:view": canSeeStructure, "locations:view": canSeeLocations };
    return [
      ...fixedSearchFields(t).filter((f) => !f.permission || allowed[f.permission]),
      ...(attributes ?? []).map(attributeSearchField),
    ];
  }, [t, attributes, canSeeStructure, canSeeLocations]);
  const fieldByName = useMemo(() => new Map(fields.map((f) => [f.name, f])), [fields]);

  // Rows whose field is unknown (an attribute deleted since the link was made) are neither shown nor sent.
  const known = useMemo(() => filters.filter((f) => fieldByName.has(f.field)), [filters, fieldByName]);
  // A range that cannot be right yet ("do 1" on the way to "do 150") is left out of the request.
  const valid = useMemo(
    () => known.filter((f) => rangeProblem(fieldByName.get(f.field)!.kind, f) === null),
    [known, fieldByName],
  );
  const hasInvalid = valid.length !== known.length;
  const requestKey = JSON.stringify(toRequestFilters(valid));
  const facetFieldsKey = known.filter((f) => fieldByName.get(f.field)?.kind !== "tree").map((f) => f.field).join("|");

  // What is searched trails what is typed, so "1", "13", "132" is one request. Nothing is
  // searched before the attribute codebook is here: until then a linked attribute criterion
  // is not known yet, and searching without it would flash the wrong rows.
  const ready = attributes !== null;
  const [appliedKey, setAppliedKey] = useState<string | null>(null);
  useEffect(() => {
    if (!ready || requestKey === appliedKey) return;
    if (appliedKey === null) { setAppliedKey(requestKey); return; }
    // While a range is half-typed the table keeps what it shows, instead of widening and narrowing again.
    if (hasInvalid) return;
    const h = setTimeout(() => setAppliedKey(requestKey), DEBOUNCE_MS);
    return () => clearTimeout(h);
  }, [ready, requestKey, appliedKey, hasInvalid]);
  const applied = useMemo<AssetFilter[]>(() => (appliedKey === null ? [] : JSON.parse(appliedKey) as AssetFilter[]), [appliedKey]);

  // Mirror the rows into the URL (the page's other parameters stay as they are).
  const encoded = encodeFilters(filters);
  useEffect(() => {
    const h = setTimeout(() => {
      setParams((prev) => {
        const next = new URLSearchParams(prev);
        if (encoded) next.set("f", encoded); else next.delete("f");
        return next;
      }, { replace: true });
    }, DEBOUNCE_MS);
    return () => clearTimeout(h);
  }, [encoded, setParams]);

  const sortKey = sortParam(sort);
  const listKey = `${appliedKey}|${sortKey}|${pageSize}`;
  const prevListKey = useRef(listKey);

  useEffect(() => {
    if (appliedKey === null) return;
    if (prevListKey.current !== listKey) {
      prevListKey.current = listKey;
      if (page !== 0) { setPage(0); return; }
    }
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    assetsApi
      .advancedSearch({ filters: applied, page, size: requestPageSize(pageSize), sort: sortKey }, controller.signal)
      .then((res) => {
        setRows(res.content);
        setTotal(res.totalElements);
        setLoading(false);
      })
      .catch((err: ApiError & { name?: string }) => {
        if (err.name === "AbortError") return;
        // The server words a refused criterion for developers; the screen says it its own way.
        setError(err.code === "VALIDATION"
          ? t("advSearch.badCriteria", "Neki filter nije ispravno unet. Proveri unete vrednosti.")
          : err.message);
        setRows((prev) => prev ?? []);
        setLoading(false);
      });
    return () => controller.abort();
    // listKey already encodes the criteria, the order and the page size.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listKey, page]);

  useEffect(() => {
    if (appliedKey === null) return;
    const controller = new AbortController();
    assetsApi
      .advancedFacets({ filters: applied, fields: facetFieldsKey ? facetFieldsKey.split("|") : [] }, controller.signal)
      .then(setFacets)
      .catch(() => { /* the table reports a bad criterion; stale choices stay until the next answer */ });
    return () => controller.abort();
    // `applied` is appliedKey parsed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedKey, facetFieldsKey]);

  // Attribute columns: what is filtered by, then everything the picked types record.
  const selectedTypeIds = useMemo(
    () => (applied.find((f) => f.field === "typeId")?.values ?? []).slice(0, MAX_TYPE_COLUMNS),
    [applied],
  );
  useEffect(() => {
    let cancelled = false;
    for (const id of selectedTypeIds) {
      if (typeDefs[id]) continue;
      assetTypesApi.attributeDefs(id)
        .then((defs) => { if (!cancelled) setTypeDefs((prev) => ({ ...prev, [id]: defs })); })
        .catch(() => { if (!cancelled) setTypeDefs((prev) => ({ ...prev, [id]: [] })); });
    }
    return () => { cancelled = true; };
    // typeDefs is only read to skip types that are already loaded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTypeIds]);

  const attributeColumns = useMemo<AttributeColumn[]>(() => {
    const out: AttributeColumn[] = [];
    const seen = new Set<string>();
    const add = (c: AttributeColumn) => { if (!seen.has(c.key)) { seen.add(c.key); out.push(c); } };
    for (const f of known) {
      const a = fieldByName.get(f.field)?.attribute;
      if (a) add({ key: a.key, label: a.label, unit: a.unit, dataType: a.dataType });
    }
    for (const id of selectedTypeIds) {
      const defs = [...(typeDefs[id] ?? [])].sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label, "sr"));
      for (const d of defs) add({ key: d.key, label: d.label, unit: d.unit, dataType: d.dataType });
    }
    return out;
  }, [known, fieldByName, selectedTypeIds, typeDefs]);

  const { iconOf } = useLocationKinds();
  const locationOptions = useMemo(
    () => locationSelectOptions(locations, { left: (e) => <LocationKindIcon icon={iconOf(e.kind)} /> }),
    [locations, iconOf],
  );

  // "Dodaj filter": fields not used yet; an attribute only while the current matches carry it.
  const addOptions = useMemo<SelectOption<string>[]>(() => {
    const groupLabel: Record<SearchFieldGroup, string> = {
      basic: t("advSearch.group.basic", "Osnovno"),
      place: t("advSearch.group.place", "Mesto"),
      replacement: t("advSearch.group.replacement", "Zamena"),
      attribute: t("advSearch.group.attribute", "Atributi"),
    };
    const used = new Set(filters.map((f) => f.field));
    const out: SelectOption<string>[] = [];
    for (const f of fields) {
      if (used.has(f.name)) continue;
      let label = fieldTitle(f);
      if (f.attribute && facets) {
        const n = facets.attributeCounts[f.attribute.key] ?? 0;
        if (n === 0) continue;
        label = `${label} · ${n}`;
      }
      out.push({ value: f.name, label, group: groupLabel[f.group], keywords: f.attribute?.key });
    }
    return out;
  }, [fields, filters, facets, t]);

  function update(index: number, next: FilterState) {
    setFilters((prev) => prev.map((f, i) => (i === index ? next : f)));
  }

  function remove(index: number) {
    setFilters((prev) => prev.filter((_, i) => i !== index));
  }

  const hasCriteria = applied.length > 0;

  return (
    <div>
      <section className="card" style={{ marginBottom: 16 }}>
        <div className="card-h" style={{ gap: 12, flexWrap: "wrap" }}>
          <div>
            <h3>{t("advSearch.filters", "Filteri")}</h3>
            <div className="sub">{t("advSearch.filtersSub", "Svi filteri važe zajedno. Tabela se sužava dok unosiš, a svaki filter nudi samo ono što ostali ostavljaju.")}</div>
          </div>
          {filters.length > 0 && (
            <button type="button" className="btn sm ghost" onClick={() => setFilters([])}>{t("advSearch.clearAll", "Očisti sve")}</button>
          )}
        </div>
        <div className="card-b">
          <div className="filter-rows">
            {filters.map((f, i) => {
              const field = fieldByName.get(f.field);
              if (!field) return null;
              return (
                <AssetFilterRow
                  key={f.field}
                  field={field}
                  filter={f}
                  facet={facets?.facets[f.field]}
                  onChange={(next) => update(i, next)}
                  onRemove={() => remove(i)}
                  locationOptions={locationOptions}
                />
              );
            })}
            <div className="filter-add">
              <Select<string>
                value=""
                options={addOptions}
                onChange={(name) => { if (name) setFilters((prev) => [...prev, { field: name }]); }}
                placeholder={t("advSearch.addFilter", "+ Dodaj filter")}
                ariaLabel={t("advSearch.addFilterAria", "Dodaj filter")}
                disabled={addOptions.length === 0}
              />
            </div>
          </div>
        </div>
      </section>

      {error && <div style={errorBox} role="alert">{error}</div>}

      <section className="card" style={{ opacity: loading && rows !== null ? 0.6 : 1, transition: "opacity .15s" }} aria-busy={loading}>
        <div className="card-h" style={{ gap: 12, flexWrap: "wrap" }}>
          <div>
            <h3>{t("advSearch.results", "Rezultat")}</h3>
            <div className="sub" aria-live="polite">
              {rows === null
                ? t("common.loading", "Učitavanje…")
                : hasCriteria
                  ? t("advSearch.matchCount", "{{count}} asseta odgovara filterima", { count: total })
                  : t("assets.count", "{{count}} asseta u registru", { count: total })}
            </div>
          </div>
          {canExport && (
            <ExportButton
              className="btn"
              path="/exports/assets.xlsx"
              body={{ filters: applied, sort: sortKey }}
              label={t("assets.export", "Izvezi u Excel")}
              filename="asseti.xlsx"
            />
          )}
        </div>
        {rows === null ? (
          <div className="empty">{t("common.loading", "Učitavanje…")}</div>
        ) : rows.length === 0 ? (
          <div className="empty">
            {hasCriteria
              ? t("assets.emptyFiltered", "Nijedan asset ne odgovara filterima.")
              : t("assets.empty", "Registar je prazan. Dodajte prvi asset.")}
          </div>
        ) : (
          <div className="tbl-scroll">
            <table className="tbl compact attr-table">
              <thead>
                <tr>
                  <SortableTh field="tag" sort={sort} onSort={setSort}>{t("assets.col.tag", "Oznaka")}</SortableTh>
                  <SortableTh field="type" sort={sort} onSort={setSort}>{t("assets.col.type", "Tip")}</SortableTh>
                  <SortableTh field="manufacturer" sort={sort} onSort={setSort}>{t("assets.col.manufacturer", "Proizvođač")}</SortableTh>
                  <SortableTh field="condition" sort={sort} onSort={setSort}>{t("assets.col.condition", "Stanje")}</SortableTh>
                  <th>{t("assets.col.usage", "Upotreba")}</th>
                  <th>{t("assets.col.position", "Logička pozicija")}</th>
                  <th>{t("assets.col.location", "Fizička lokacija")}</th>
                  {attributeColumns.map((c) => (
                    <SortableTh key={c.key} field={`${ATTRIBUTE_PREFIX}${c.key}`} sort={sort} onSort={setSort}>
                      {c.label}{c.unit && <span className="muted"> ({c.unit})</span>}
                    </SortableTh>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => {
                  const pos = a.positionId ? shortPathLabel(a.positionPath, a.positionName) : null;
                  return (
                    <tr key={a.id}>
                      <td className="mono">
                        <Link to={`/asseti/${a.id}`}><b>{a.tag}</b></Link>
                        {a.serialNo && <span className="muted" style={{ display: "block", fontSize: 11.5 }}>{a.serialNo}</span>}
                      </td>
                      <td><span className="mono" style={{ fontSize: 12, color: "var(--ink-3)" }}>{a.typeCode}</span> {a.typeName}</td>
                      <td>
                        {a.manufacturer ?? <span className="muted">—</span>}
                        {a.manufacturerCode && <span className="muted" style={{ display: "block", fontSize: 11.5 }}>{a.manufacturerCode}</span>}
                      </td>
                      <td><ConditionChip condition={a.condition} /></td>
                      <td><UsageChip usage={a.usage} /></td>
                      <td>
                        {pos && a.positionId
                          ? <PermLink permission="structure:view" to={`/struktura/${a.positionId}`} title={pos.full}>{pos.short}</PermLink>
                          : <span className="muted">—</span>}
                      </td>
                      <td>{a.effectiveLocation ? a.effectiveLocation.name : <span className="muted">—</span>}</td>
                      {attributeColumns.map((c) => (
                        <td key={c.key} className={c.dataType === "NUMBER" ? "num" : undefined}>
                          {formatValue(t, c, a.actualValues?.[c.key])}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
      <TableFooter
        page={page}
        pageSize={pageSize}
        total={total}
        onPage={setPage}
        onPageSize={(s) => { storePageSize(PAGE_SIZE_KEY, s); setPageSize(s); }}
        allLimit={SERVER_ALL_LIMIT}
      />
      {!hasCriteria && rows !== null && rows.length > 0 && (
        <p className="muted" style={{ fontSize: 12.5, marginTop: 8 }}>
          <Ic.filter aria-hidden="true" style={{ width: 13, height: 13, verticalAlign: "-2px", marginRight: 4 }} />
          {t("advSearch.hint", "Dodaj filter iznad — na primer tip dela, pa snagu od–do i stanje.")}
        </p>
      )}
    </div>
  );
}

const errorBox: CSSProperties = { padding: 10, background: "rgba(182,53,58,.08)", color: "var(--err)", borderRadius: 6, fontSize: 13, marginBottom: 12 };
